<template>
  <figure class="notification" v-on:click="executeAction">
    <div
      class="notification__body"
      :class="{
        'notification__body--success': type === 'success',
        'notification__body--warning': type === 'warning',
        'notification__body--error': type === 'error',
        'notification__body--info': type === 'info',
      }"
    >
      <figcaption class="notification__title">{{ title }}</figcaption>
      <p class="notification__message" v-html="message"></p>
    </div>
    <menu class="notification__menu">
      <menuitem class="notification__menu-item" v-if="actionName">{{
        actionName
      }}</menuitem>
      <menuitem
        class="notification__menu-item notification__menu-item--close"
        v-on:click.stop="close"
        >{{ labelClose }}</menuitem
      >
    </menu>
  </figure>
</template>

<script>
/**
 * @group Notifications
 * See Notification Rail on how to use Notifications.
 * This is just the notification block, it's a really simple component that just shows a message.
 */

import BaseComponent from "../BaseComponent";

export default {
  name: "Notification",
  mixins: [BaseComponent],
  props: {
    // The type defines the colour and the bold label in the notification
    type: {
      // `'Success'` / `'Error'` / `'Warning'`  / `'Hint'` / `''`
      type: String,
    },
    // The main message of the notification.
    message: {
      type: String,
      required: true,
    },
    // When the notification has an action, this will be the label for it's button
    actionName: {
      required: false,
    },
    // The default title is the same as the type name. (Success, Error, etc..), this overwrites
    customTitle: {
      required: false,
    },
    // An action that's executed when clicking on the notification.
    action: {
      required: false,
      type: Function,
    },
    labelClose: {
      type: String,
      default: "close",
    },
  },
  computed: {
    title() {
      let title;
      if (this.customTitle) {
        title = this.customTitle;
      } else {
        switch (this.type) {
          case "success":
            title = "Success";
            break;
          case "error":
            title = "Error";
            break;
          case "warning":
            title = "Warning";
            break;
          case "info":
            title = "Hint";
            break;
          default:
            title = "";
        }
      }
      return title;
    },
  },
  methods: {
    close() {
      this.$emit("requestClose");
    },
    // @vuese
    executeAction() {
      if (!this.action) return;
      this.action();
      this.$emit("requestClose");
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../style_variables/style_variables.scss";
.notification {
  @include elevationLevel(3);
  display: flex;
  width: 340px;
  margin: 12px;
  border-radius: 4px;
  overflow: hidden;
  background-color: $white;
}
.notification__body {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  height: 100%;
  padding: 20px 12px 24px 24px;
  border-left: 3px solid $grey_bombay;

  &.notification__body--success {
    border-left-color: $green;
  }
  &.notification__body--warning {
    border-left-color: $orange;
  }
  &.notification__body--error {
    border-left-color: $red;
  }
  &.notification__body--info {
    border-left-color: $blue;
  }
}
.notification__title {
  @extend %body1_style;
  font-weight: 700;
}
.notification__message {
  @extend %body2_style;
  margin: 0px;
}
.notification__menu {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  flex-shrink: 0;
  width: 84px;
  padding: 0;
  margin: 0;
  border-left: 1px solid $grey_athens;
}
.notification__menu-item {
  @extend %button_typography_style;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  margin: 0px;

  &:hover {
    cursor: pointer;
    background-color: $grey_bg;
  }

  &.notification__menu-item--close {
    font-weight: 500;
    color: $grey_bombay;
  }
  & ~ .notification__menu-item {
    border-top: 1px solid $grey_alabaster;
  }
}
</style>
